import { useEffect, useState } from "react";
import { DeskBlockOverlay } from "../blocker/DeskBlockOverlay";
import { useBlockedUrlOverlay } from "../../hooks/useBlockedUrlOverlay";
import { EmbeddedBrowserFrame } from "../layout/EmbeddedBrowserFrame";
import type { TradingPlatform } from "../TradingBrowser";
import { BrowserControls } from "./browser-controls";

interface BrowserPaneProps {
  activePlatform: TradingPlatform;
  activeUrl: string;
  resolveUrl: (platform: TradingPlatform) => string;
  resolveLabel: (platform: TradingPlatform) => string;
  frameBg: string;
  reloadNonce: number;
}

interface BrowserNavState {
  url: string;
  canGoBack: boolean;
  canGoForward: boolean;
}

export function BrowserPane({
  activePlatform,
  activeUrl,
  resolveUrl,
  resolveLabel,
  frameBg,
  reloadNonce,
}: BrowserPaneProps) {
  const [visitedPlatforms, setVisitedPlatforms] = useState<TradingPlatform[]>(
    () => (activeUrl ? [activePlatform] : []),
  );
  const [frameUrls, setFrameUrls] = useState<Record<string, string>>({});
  const [activeFrame, setActiveFrame] = useState<any>(null);
  const [activeFrameReady, setActiveFrameReady] = useState(false);
  const [localReloadNonce, setLocalReloadNonce] = useState(0);
  const [navState, setNavState] = useState<BrowserNavState>({
    url: activeUrl,
    canGoBack: false,
    canGoForward: false,
  });
  const currentUrl = frameUrls[activePlatform] ?? activeUrl;
  const { shouldOverlay } = useBlockedUrlOverlay(currentUrl);

  useEffect(() => {
    if (!activeUrl) return;
    setVisitedPlatforms((prev) =>
      prev.includes(activePlatform) ? prev : [...prev, activePlatform],
    );
    setFrameUrls((prev) => ({
      ...prev,
      [activePlatform]: prev[activePlatform] ?? activeUrl,
    }));
    setNavState({
      url: activeUrl,
      canGoBack: false,
      canGoForward: false,
    });
  }, [activePlatform, activeUrl]);

  const navigateTo = (url: string) => {
    setFrameUrls((prev) => ({ ...prev, [activePlatform]: url }));
    setNavState((prev) => ({ ...prev, url }));
  };

  const reload = () => {
    if (activeFrameReady && typeof activeFrame?.reload === "function") {
      try {
        activeFrame.reload();
      } catch {
        setLocalReloadNonce((value) => value + 1);
      }
      return;
    }
    setLocalReloadNonce((value) => value + 1);
  };

  useEffect(() => {
    if (!activeFrame) return;
    setActiveFrameReady(false);

    const syncState = () => {
      const nextUrl = readFrameUrl(activeFrame, currentUrl);
      const url = nextUrl || currentUrl;
      setFrameUrls((prev) => ({ ...prev, [activePlatform]: url }));
      setNavState({
        url,
        canGoBack: readFrameBoolean(activeFrame, "canGoBack"),
        canGoForward: readFrameBoolean(activeFrame, "canGoForward"),
      });
    };
    const markReady = () => {
      setActiveFrameReady(true);
      syncState();
    };

    const events = ["did-navigate", "did-navigate-in-page", "load"];
    events.forEach((eventName) =>
      activeFrame.addEventListener?.(eventName, syncState),
    );
    activeFrame.addEventListener?.("dom-ready", markReady);

    return () => {
      events.forEach((eventName) =>
        activeFrame.removeEventListener?.(eventName, syncState),
      );
      activeFrame.removeEventListener?.("dom-ready", markReady);
    };
  }, [activeFrame, activePlatform, currentUrl]);

  useEffect(() => {
    if (reloadNonce <= 0) return;
    reload();
  }, [reloadNonce]);

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden">
      <BrowserControls
        baseUrl={activeUrl}
        currentUrl={navState.url || currentUrl}
        canGoBack={activeFrameReady && navState.canGoBack}
        canGoForward={activeFrameReady && navState.canGoForward}
        onBack={() => {
          if (!activeFrameReady) return;
          activeFrame?.goBack?.();
        }}
        onForward={() => {
          if (!activeFrameReady) return;
          activeFrame?.goForward?.();
        }}
        onReload={reload}
        onNavigate={navigateTo}
      />
      <div className="relative min-h-0 flex-1 overflow-hidden">
        {shouldOverlay ? <DeskBlockOverlay visible /> : null}
        {visitedPlatforms.map((platform) => {
          const platformBaseUrl = resolveUrl(platform);
          const url = frameUrls[platform] ?? platformBaseUrl;
          if (!url) return null;
          const isActive = platform === activePlatform;
          return (
            <div
              key={platform}
              className="absolute inset-0"
              style={{ display: isActive ? "block" : "none" }}
            >
              <EmbeddedBrowserFrame
                ref={isActive ? setActiveFrame : undefined}
                key={`${platform}:${isActive ? localReloadNonce : 0}`}
                title={resolveLabel(platform)}
                src={url}
                className={`h-full w-full ${frameBg}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function readFrameUrl(frame: any, fallbackUrl: string): string {
  try {
    if (typeof frame.getURL === "function")
      return frame.getURL() || fallbackUrl;
  } catch {
    return frame.src || fallbackUrl;
  }
  return frame.src || fallbackUrl;
}

function readFrameBoolean(frame: any, method: "canGoBack" | "canGoForward") {
  try {
    return typeof frame[method] === "function" ? frame[method]() : false;
  } catch {
    return false;
  }
}
